import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

type ErrorBoundaryProps = {
  children: ReactNode;
  message?: ReactNode;
  /** When any of these values change, the error state resets automatically */
  resetKeys?: unknown[];
};

type ErrorBoundaryState = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && this.props.resetKeys && prevProps.resetKeys) {
      const changed = this.props.resetKeys.some((key, i) => key !== prevProps.resetKeys![i]);
      if (changed) {
        this.setState({ hasError: false });
      }
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { ...errorInfo } });
    } else {
      console.error('Captured by ErrorBoundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            margin: '2rem auto',
            padding: '2rem',
            maxWidth: 420,
            background: '#424242',
            border: '1px solid #616161',
            borderRadius: 8,
            color: '#BDBDBD',
            fontFamily: 'inherit',
            textAlign: 'center',
          }}
        >
          <h2>Something went wrong.</h2>
          <p>
            {this.props.message ? (
              this.props.message
            ) : (
              <>
                Sorry, something went wrong while displaying this part of the app.
                <br />
                <br />
                Our team has been notified and is already working on a fix.
                <br />
                Please try refreshing the page. If it does not help, come back later or reach out in our Discord for help.
              </>
            )}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
